/**
 * App Layout (Client-side)
 *
 * This is the main client-side layout that wraps the entire application.
 * It includes all providers, global components, and routing logic.
 */

import "@/styles/globals.scss";
import "react-loading-skeleton/dist/skeleton.css";
import { Analytics } from "@vercel/analytics/react";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { getBasePath } from "@/lib/apiConfig";
import { LocaleDirectionProvider } from "@/providers/DirectionProvider";
import { IntlProvider } from "@/providers/IntlProvider";
import { LocalQueryClientProvider } from "@/providers/LocalQueryClientProvider";
import React, { Suspense, lazy } from "react";
import SegmentAnalytics from "@/components/SegmentAnalytics";
import GTagHeader from "@/components/GTagHeader";
import { SkeletonTheme } from "react-loading-skeleton";
import { grayColors } from "@/lib/appColors";
import { isDevOrStaging } from "@/lib/utils";
import { detectLocale } from "@/lib/locale-detection";
import { adjustKeys, loadMessages } from "@/lib/i18n-config";
import { BrowserRouter, useLocation, useNavigate } from "react-router-dom";
import { Satoshi } from "@/components/fonts/satoshi";

const AppDialogs = lazy(() => import("@/components/dialog/AppDialogs"));
const AppRouter = lazy(() => import("@/router/app-router"));
/**
 * @deprecated for the TV, we need the exact hash and search params, so we can't normalize the URL
 * @param children
 * @constructor
 */
function UrlNormalizer({ children }: { children: React.ReactNode }) {
    const location = useLocation();
    const navigate = useNavigate();
    const lowercasePath = location.pathname.toLowerCase();

    React.useEffect(() => {
        if (location.pathname !== lowercasePath) {
            navigate(lowercasePath + location.search + location.hash, { replace: true });
        }
    }, [location.pathname, lowercasePath, location.search, location.hash, navigate]);

    if (location.pathname !== lowercasePath) {
        return null;
    }

    return <>{children}</>;
}

// Wrapper to signal react-router is ready before it registers its popstate listener
function ReactRouterWrapper({ children }: { children: React.ReactNode }) {
    // Set flag synchronously before BrowserRouter mounts and registers its listener
    if (typeof window !== "undefined") {
        (window as any).__REACT_ROUTER_READY__ = true;
    }
    return <BrowserRouter basename={getBasePath()}>{children}</BrowserRouter>;
}

export default function AppLayout() {
    // Direct locale detection without hooks (hooks need IntlProvider)
    // Initialize with default values to avoid SSR issues
    const [locale] = React.useState(() => (typeof window !== "undefined" ? detectLocale() : "en"));
    const [messages] = React.useState<Record<string, any>>(() => {
        if (typeof window !== "undefined") {
            const rawMessages = loadMessages(locale);
            return adjustKeys(rawMessages);
        }
        return {};
    });

    const isRTL = locale === "he";
    const dir = isRTL ? "rtl" : "ltr";

    // Get host from window in client component
    const [host] = React.useState(() => (typeof window !== "undefined" ? window.location.host : ""));

    React.useEffect(() => {
        // Update html lang, dir, and body class
        if (typeof document !== "undefined") {
            document.documentElement.lang = locale;
            document.documentElement.dir = dir;
            document.body.className = Satoshi.className;
        }
    }, [locale, dir]);

    React.useEffect(() => {
        if (typeof document !== "undefined") {
            document.title = messages["AppTitle"] || "PBX";
        }
    }, [messages]);

    const isDevOrStagingEnv = isDevOrStaging(host);

    return (
        <>
            {/* GTM Head - inject into existing head */}
            {!isDevOrStagingEnv && typeof document !== "undefined" && <GTagHeader />}

            {/* GTM noscript */}
            {!isDevOrStagingEnv && (
                <noscript>
                    <iframe
                        src="https://www.googletagmanager.com/ns.html?id=GTM-PKKG2DPP"
                        height="0"
                        width="0"
                        style={{ display: "none", visibility: "hidden" }}
                    ></iframe>
                </noscript>
            )}

            <IntlProvider>
                <LocalQueryClientProvider>
                    <TooltipProvider>
                        <LocaleDirectionProvider>
                            <SkeletonTheme baseColor={grayColors["50"]} highlightColor={grayColors["200"]}>
                                <ReactRouterWrapper>
                                    <Suspense fallback={null}>
                                        <AppRouter />
                                    </Suspense>
                                </ReactRouterWrapper>
                            </SkeletonTheme>
                        </LocaleDirectionProvider>
                    </TooltipProvider>
                </LocalQueryClientProvider>
                <Suspense fallback={null}>
                    <AppDialogs />
                </Suspense>
            </IntlProvider>

            <Toaster />

            {/* Only load analytics in production */}
            {!isDevOrStagingEnv && <SegmentAnalytics />}

            <Analytics debug={false} />
        </>
    );
}
